import { transition } from '@helpers/animate';
import brand, { BrandColours } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import { contentStyles, h2, subheading } from '@helpers/typography';
import styled from 'styled-components';
const ContentWrapper = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;

  [data-layout='central'] &,
  [data-layout='split'] & {
    max-width: 546px;
  }

  [data-layout='central'] & {
    margin-left: auto;
    margin-right: auto;
  }
`;
const Container = styled.section `
  --headingMargin: ${fluid(8, 14)};

  display: flex;
  flex-direction: column;

  &[data-layout='default'],
  &[data-layout='central'] {
    max-width: 695px;
  }

  &[data-layout='central'] {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  &:not(:has(${ContentWrapper})) {
    --headingMargin: 0px;
  }

  @media ${from(Device.TabletLarge)} {
    &[data-layout='central'] {
      align-items: center;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }

    &[data-layout='split'] {
      --headingMargin: 0px;

      flex-direction: row;
      justify-content: space-between;
      column-gap: ${fluid(56, 120)};
    }
  }
`;
const HeadIcon = styled.div `
  display: none;

  [data-layout='central'] & {
    display: block;

    svg {
      width: ${fluid(48, 65)};
      height: ${fluid(48, 65)};
    }
  }
`;
const Subheading = styled.span `
  ${subheading};

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-layout='split'] & {
      grid-column: 1 / span 1;
    }
  }
`;
const Heading = styled.h2 `
  ${h2};

  text-wrap: pretty;
  margin-bottom: var(--headingMargin);
  color: var(--headingColour, currentColor);

  ${Container}[data-layout='split'] & {
    max-width: 695px;
  }

  > span {
    ${transition('background-size, color', 0.5)};

    display: inline;
    background-image: linear-gradient(
      to right,
      var(--highlightColour, transparent),
      var(--highlightColour, transparent)
    );
    background-repeat: no-repeat;
    background-size: 100% 100%;
    transition-delay: 200ms;
    color: var(--highlightTextColour, currentColor);
    padding: 0 8px;
    margin-left: -8px;

    ${Container}[data-animate='false'] & {
      background-size: 0% 100%;
      color: currentColor;
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-layout='split'] & {
      grid-column: 1 / span 1;
    }
  }
`;
const LogoImage = styled.img `
  display: block;
`;
const Content = styled.div `
  ${contentStyles};

  [data-theme=${BrandColours.PurpleDark}] &,
  [data-theme=${BrandColours.PurpleLight}] & {
    ol,
    ul {
      li::before {
        color: ${brand.global.white};
      }
    }
  }
`;
const ButtonWrapper = styled.div `
  display: flex;
  gap: var(--headerBlockButtonsGap, ${fluid(16, 38)});
  flex-wrap: wrap;
  margin-top: ${fluid(16, 38)};
`;
const DetailContent = styled.div `
  ${transition('transform, opacity', 0.5)};
  ${fonts.lato.semiBold};

  font-size: ${fluid(15, 16)};
  line-height: ${fluid(23, 26)};
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  margin-top: ${fluid(10, 14)};

  &:not(:last-child) {
    margin-bottom: ${fluid(10, 14)};
  }

  @media print {
    opacity: 1 !important;
    transform: translateY(0px) !important;
  }

  [data-animate='false'] & {
    opacity: 0;
    transform: translateY(20px);

    @media print {
      opacity: 1 !important;
      transform: translateY(0px) !important;
    }
  }

  [data-secondary=${BrandColours.White}] & {
    color: ${brand.grey.dark};
  }

  ::before {
    content: '';
    display: block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${brand.global.white};
    position: absolute;
    top: 50%;
    right: calc((37px / 2) * -1);
    transform: translateY(-50%);

    [data-secondary=${BrandColours.White}] & {
      background-color: ${brand.grey.dark};
    }
  }

  :last-child::before {
    content: none;
  }
`;
const Detail = styled.span `
  display: inline-flex;
  align-items: center;
  gap: ${fluid(4, 8)};
  position: relative;

  svg {
    width: 24px;
    height: 24px;

    [data-secondary=${BrandColours.White}] & {
      color: ${brand.primary.purple.light};
    }
  }
`;
const HeadingSummaryBlockStyles = {
    Container,
    HeadIcon,
    Heading,
    Subheading,
    LogoImage,
    Content,
    ContentWrapper,
    ButtonWrapper,
    DetailContent,
    Detail,
};
export default HeadingSummaryBlockStyles;
